import {Divider, Grid, Typography} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {A360ProductCd} from 'Quoting/models/Quoting';
import {Map as ImmutableMap} from 'immutable';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {GridRow, GridRowProps} from '../../../../shared/GridRow';
import {QuoteFormElement} from '../../../../shared/QuoteFormElement';
import {FormWrapper} from '../../../../shared/QuoteFormWrapper';
import {getEligibilityPropertyPaths} from './PropertyPaths';

export const EligibilityBlock = () => {
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const productCd = selectedProduct?.code ?? '';

  const propertyPaths = getEligibilityPropertyPaths(selectedPlan?.planIndex, 0, selectedProduct?.code);

  const isGCI5Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) === 'GCI5';

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'eligibility-row-1'},
      title: 'CENSUS',
      hidden: true,
      items: [
        {
          key: propertyPaths.censusTypeCd,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}CensusTypeCd`}
              label="Census Type"
              value={getQuoteFieldValue(propertyPaths.censusTypeCd)}
              id={propertyPaths.censusTypeCd}
              onChange={e => updateQuoteFieldValue(propertyPaths.censusTypeCd, e.target.value as string)}
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.assumedParticipationPct,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Assumed Participation %"
              id={propertyPaths.assumedParticipationPct}
              value={getQuoteFieldValue(propertyPaths.assumedParticipationPct)}
              onChange={e => updateQuoteFieldValue(propertyPaths.assumedParticipationPct, e.target.value)}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'eligibility-row-2'},
      title: 'NEW PARTICIPANTS',
      items: [
        {
          key: propertyPaths.newParticipant.minHourlyReq,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Minimum Hourly Requirement (Hours Per Week)"
              type="number"
              id={propertyPaths.newParticipant.minHourlyReq}
              value={getQuoteFieldValue(propertyPaths.newParticipant.minHourlyReq)}
              onChange={e => updateQuoteFieldValue(propertyPaths.newParticipant.minHourlyReq, e.target.value)}
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.waitingPeriodDefCd,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Eligibility Waiting Period Definition"
              id={propertyPaths.waitingPeriodDefCd}
              value={getQuoteFieldValue(propertyPaths.waitingPeriodDefCd)}
              onChange={e => updateQuoteFieldValue(propertyPaths.waitingPeriodDefCd, e.target.value)}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.waitingPeriodAmount,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Waiting Period"
              id={propertyPaths.waitingPeriodAmount}
              value={getQuoteFieldValue(propertyPaths.waitingPeriodAmount)}
              onChange={e => updateQuoteFieldValue(propertyPaths.waitingPeriodAmount, e.target.value)}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.waitingPeriodModeCd,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}`}
              label="Waiting Period Mode"
              id={propertyPaths.waitingPeriodModeCd}
              value={getQuoteFieldValue(propertyPaths.waitingPeriodModeCd)}
              onChange={e => updateQuoteFieldValue(propertyPaths.waitingPeriodModeCd, e.target.value)}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'eligibility-row-3'},
      title: 'ISSUE AGES',
      hidden: ![A360ProductCd.HI, A360ProductCd.ACC].includes(productCd as A360ProductCd),
      items: [
        {
          key: propertyPaths.issuesAges.minIssueAgeIndividual,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Minimum Issue Age - Participant"
              type="number"
              id={propertyPaths.issuesAges.minIssueAgeIndividual}
              value={getQuoteFieldValue(propertyPaths.issuesAges.minIssueAgeIndividual)}
              onChange={e => updateQuoteFieldValue(propertyPaths.issuesAges.minIssueAgeIndividual, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.issuesAges.minIssueAgeChild,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Minimum Issue Age - Children"
              id={propertyPaths.issuesAges.minIssueAgeChild}
              value={getQuoteFieldValue(propertyPaths.issuesAges.minIssueAgeChild)}
              onChange={e => updateQuoteFieldValue(propertyPaths.issuesAges.minIssueAgeChild, e.target.value)}
              disabled
              required={productCd !== A360ProductCd.HI}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.issuesAges.maxIssueAgeChild,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Issue Through Age - Children"
              id={propertyPaths.issuesAges.maxIssueAgeChild}
              value={getQuoteFieldValue(propertyPaths.issuesAges.maxIssueAgeChild)}
              onChange={e => updateQuoteFieldValue(propertyPaths.issuesAges.maxIssueAgeChild, e.target.value)}
              disabled
              required={productCd !== A360ProductCd.HI}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'eligibility-row-3'},
      title: 'ISSUE AGES',
      hidden: !isGCI5Plan,
      items: [
        {
          key: propertyPaths.issueAge.minIssueAgeIndividual,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Minimum Issue Age - Participant"
              type="number"
              id={propertyPaths.issueAge.minIssueAgeIndividual}
              value={getQuoteFieldValue<number>(propertyPaths.issueAge.minIssueAgeIndividual)}
              onChange={e => updateQuoteFieldValue(propertyPaths.issueAge.minIssueAgeIndividual, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.issueAge.minIssueAgeChild,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Minimum Issue Age - Children"
              id={propertyPaths.issueAge.minIssueAgeChild}
              value={getQuoteFieldValue(propertyPaths.issueAge.minIssueAgeChild)}
              onChange={e => updateQuoteFieldValue(propertyPaths.issueAge.minIssueAgeChild, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: propertyPaths.issueAge.maxIssueAgeChild,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Issue Through Age - Children"
              id={propertyPaths.issueAge.maxIssueAgeChild}
              value={getQuoteFieldValue(propertyPaths.issueAge.maxIssueAgeChild)}
              onChange={e => updateQuoteFieldValue(propertyPaths.issueAge.maxIssueAgeChild, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Eligibility" collapsible withSeparator>
      <Grid container alignItems="center" style={{maxWidth: '100%'}}>
        {rows.map(
          ({items, rowProps, title, hidden}, index) =>
            !hidden && (
              <React.Fragment key={rowProps?.key}>
                {title && (
                  <>
                    <Typography variant="body1" style={{paddingLeft: '15px'}} className="lbl-main">
                      {title}
                    </Typography>
                    <Grid item xs={12}>
                      <Divider style={{width: '100%', margin: '10px 0', color: '#2F3847'}} />
                    </Grid>
                  </>
                )}
                <GridRow items={items} rowProps={rowProps} />
              </React.Fragment>
            )
        )}
      </Grid>
    </FormWrapper>
  );
};
